// Type definitions for RFQ Processing System

export enum RFQStatus {
    WAITING_FOR_DOCUMENTS = "waiting_for_documents",
    WAITING_FOR_CLIENT_FEEDBACK = "waiting_for_client_feedback",
    COMPLETED = "completed"
}

export interface RFQ {
    id: string;
    title: string;
    description?: string;
    client_name: string;
    part_type?: string;
    status: RFQStatus;
    created_at: string;
    updated_at: string;
}

export interface RFQCreate {
    title: string;
    description?: string;
    client_name: string;
    part_type?: string;
}

export interface RFQUpdate {
    title?: string;
    description?: string;
    client_name?: string;
    part_type?: string;
    status?: RFQStatus;
}

export interface RFQEquipmentLineItem {
    name: string;
    quantity: number;
}

export interface RFQDocumentExtractedData {
    general_requirements: string[];
    sequence_of_operations: string[];
    required_equipment: RFQEquipmentLineItem[];
    free_issued_equipment: RFQEquipmentLineItem[];
    equipment_approved_for_use: RFQEquipmentLineItem[];
}

export interface RFQDocumentUploadResponse {
    id: string;
    rfq_id: string;
    filename: string;
    uploaded_at: string;
    extracted_data: RFQDocumentExtractedData;
}

// Conveyor stations (predefined types + per-RFQ config)

export interface PredefinedStation {
    id: string;
    name: string;
}

export interface StationEquipmentLine {
    equipment_id: string;
    quantity: number;
    is_free_issued?: boolean;
    from_process_template_id?: string | null;
}

export type WorkerType = 'internal' | 'external';

export interface SetupLaborEntry {
    role: string;
    hours: number;
}

export interface StationSetupLaborEntry {
    role: string;
    hours: number;
    worker_type: WorkerType;
    from_process_template_id?: string | null;
}

export interface ConveyorStationStep {
    id: string;
    text: string;
    duration_seconds: number;
}

export interface ConveyorStationConfig {
    station_type_id: string;
    name?: string;
    comments?: string;
    steps?: ConveyorStationStep[];
    equipment?: StationEquipmentLine[];
    setup_labor?: StationSetupLaborEntry[];
}

// Cost-enriched response returned by GET /api/rfqs/{id}/conveyor-stations

export interface StationEquipmentLineCost {
    equipment_id: string;
    equipment_name: string;
    quantity: number;
    is_free_issued: boolean;
    unit_cost: number;
    goods: number;
    total: number;
    from_process_template_id?: string | null;
}

export interface StationSetupLaborCost {
    role: string;
    role_name: string;
    hours: number;
    rate: number;
    amount: number;
    worker_type: WorkerType;
    from_process_template_id?: string | null;
}

export interface ConveyorStationWithCost {
    station_type_id: string;
    name?: string;
    comments?: string;
    steps: ConveyorStationStep[];
    equipment: StationEquipmentLineCost[];
    setup_labor: StationSetupLaborCost[];
    total_goods: number;
    total_labor: number;
    total: number;
}

// Per-RFQ program parameters

export interface RFQProgramParams {
    quote_rev?: string | null;
    takt_seconds?: number | null;
    annual_volume?: number | null;
    volume_note?: string | null;
    shifts_per_day?: number | null;
    hours_per_shift?: number | null;
    operators?: number | null;
    equipment_delivery?: string | null;
    sop_date?: string | null;
}

// Inventory: equipment catalog and labor rates

export interface LaborRate {
    role: string;
    name: string;
    internal_rate: number;
    external_rate: number;
}

export interface Equipment {
    id: string;
    name: string;
    category?: string;
    unit_cost: number;
}

// Process templates: admin-managed bundles of equipment lines + setup labor

export interface ProcessTemplate {
    id: string;
    name: string;
    category?: string | null;
    description?: string | null;
    items: StationEquipmentLine[];
    setup_labor: SetupLaborEntry[];
}

export interface ProcessTemplateCreate {
    id: string;
    name: string;
    category?: string | null;
    description?: string | null;
    items: StationEquipmentLine[];
    setup_labor: SetupLaborEntry[];
}

export interface ProcessTemplateUpdate {
    name: string;
    category?: string | null;
    description?: string | null;
    items: StationEquipmentLine[];
    setup_labor: SetupLaborEntry[];
}

// Procurement

export enum ProcurementStatus {
    PENDING = "pending",
    QUOTED = "quoted",
    ORDERED = "ordered",
    RECEIVED = "received",
}

export interface Vendor {
    id: string;
    name: string;
    contact?: string;
}

export interface ProcurementLine {
    equipment_id: string;
    equipment_name: string;
    quantity: number;
    expected_unit_cost: number;
    expected_total: number;
    vendor?: string | null;
    po_number?: string | null;
    status: ProcurementStatus;
    actual_unit_cost?: number | null;
    actual_total?: number | null;
    expected_delivery?: string | null;
    received_at?: string | null;
    notes?: string | null;
    updated_at?: string | null;
    is_orphaned: boolean;
}

export interface ProcurementLineUpdate {
    vendor?: string | null;
    po_number?: string | null;
    status?: ProcurementStatus;
    actual_unit_cost?: number | null;
    expected_delivery?: string | null;
    received_at?: string | null;
    notes?: string | null;
}

export interface ProcurementSummary {
    total_lines: number;
    ordered_lines: number;
    received_lines: number;
    expected_total: number;
    actual_total: number;
}

export interface ProcurementResponse {
    lines: ProcurementLine[];
    summary: ProcurementSummary;
}

// Conveyor drawing (DXF) extraction

export interface ExtractedRobot {
    code: string;
    station_code: string;
    type_code?: string | null;
    type_description?: string | null;
    position: [number, number];
}

export interface ExtractedEquipmentCandidate {
    block_name: string;
    quantity: number;
    position: [number, number];
    matched_equipment_id?: string | null;
    matched_process_template_id?: string | null;
    match_confidence: number;
}

export interface ExtractedStation {
    code: string;
    description?: string | null;
    position: [number, number];
    suggested_station_type_id?: string | null;
    robots: ExtractedRobot[];
    equipment: ExtractedEquipmentCandidate[];
}

export interface ExtractedConveyorLayout {
    stations: ExtractedStation[];
    unassigned_equipment: ExtractedEquipmentCandidate[];
    warnings: string[];
    drawing_extents: [number, number, number, number];
    units: string;
}

export interface DrawingEquipmentSelection {
    kind: 'equipment';
    equipment_id: string;
    quantity: number;
    is_free_issued: boolean;
}

export interface DrawingProcessSelection {
    kind: 'process';
    process_template_id: string;
    quantity: number;
}

export type DrawingRowSelection =
    | DrawingEquipmentSelection
    | DrawingProcessSelection;

export interface StationFromDrawing {
    station_type_id: string;
    name?: string | null;
    comments?: string | null;
    selections: DrawingRowSelection[];
}

export interface ConveyorDrawingApplyPayload {
    stations: StationFromDrawing[];
}
